import { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion, AnimatePresence } from "motion/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { configSecondaryInfo } from "@/features/evento/eventoThunks";

export default function EventTags({
  availableTags = [],
  initialSelectedTags = [],
  onSave,
  eventIsPublished,
}) {
  const dispatch = useDispatch();
  const { idEvento } = useParams();
  const { isLoading: eventoLoading } = useSelector((state) => state.evento);

  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Solo permitir una categoría ya que la base de datos usa ENUM (valor único)
  const [selectedTag, setSelectedTag] = useState(initialSelectedTags[0] || null);
  const [previewTag, setPreviewTag] = useState(initialSelectedTags[0] || null);

  useEffect(() => {
    if (initialSelectedTags && initialSelectedTags.length > 0) {
      setSelectedTag(initialSelectedTags[0]);
      setPreviewTag(initialSelectedTags[0]);
    }
  }, [initialSelectedTags]);

  const filteredTags = useMemo(() => {
    if (!searchTerm) return availableTags;
    return availableTags.filter((tag) =>
      tag.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableTags, searchTerm]);

  const handleToggleTag = (tag) => {
    // Solo permitir selección única
    if (selectedTag === tag) {
      setSelectedTag(null);
    } else {
      setSelectedTag(tag);
    }
  };

  const handleSave = async () => {
    if (!idEvento) {
      console.error("No se encontró el ID del evento");
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const configData = {
        categoriaEvento: selectedTag,
      };

      console.log("Guardando categoría del evento:", configData);

      await dispatch(
        configSecondaryInfo({
          uidEvento: idEvento,
          configData,
        })
      ).unwrap();

      setPreviewTag(selectedTag);
      setIsEditMode(false);
      setSaveSuccess(true);

      if (onSave) {
        onSave(selectedTag ? [selectedTag] : []);
      }

      // Mostrar mensaje de éxito por unos segundos
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error al guardar categoría:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedTag(previewTag);
    setIsEditMode(false);
  };

  const handleClearSelection = () => {
    setSelectedTag(null);
  };

  return (
    <div className="relative bg-aw-bluedark-95 rounded-2xl p-4 sm:p-6 flex flex-col items-center w-full">
      {/* Edit/Preview Toggle Button */}
      {isEditMode ? (
        <></>
      ) : !eventIsPublished ? (
        <Motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsEditMode(!isEditMode)}
          className="absolute top-4 right-4 z-50 px-4 py-2 rounded-full bg-aw-green/85 text-aw-bluedark font-medium hover:bg-aw-green transition duration-300 flex items-center gap-2"
        >
          <FontAwesomeIcon
            icon={isEditMode ? "fa-solid fa-eye" : "fa-solid fa-edit"}
          />
          {isEditMode ? "Vista Previa" : "Editar"}
        </Motion.button>
      ) : (
        <></>
      )}

      <AnimatePresence mode="wait">
        {isEditMode ? (
          <Motion.div
            key="edit-mode"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full pt-12 space-y-6"
          >
            <div className="text-white font-bold text-xl text-center mb-4">
              Categorías de Evento
            </div>

            {/* Search Input */}
            <div className="relative">
              <FontAwesomeIcon
                icon="fa-solid fa-search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar categorías..."
                className="w-full pl-10 pr-4 py-3 bg-aw-bluedark border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-aw-green transition duration-300"
              />
            </div>

            {/* Selected Tag Section */}
            {selectedTag && (
              <div className="bg-aw-bluedark border border-aw-green rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-check-circle"
                      className="text-aw-green"
                    />
                    Categoría Seleccionada
                  </h4>
                  <button
                    onClick={handleClearSelection}
                    className="text-xs text-red-400 hover:text-red-300 transition duration-300"
                  >
                    <FontAwesomeIcon
                      icon="fa-solid fa-trash"
                      className="mr-1"
                    />
                    Limpiar
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Motion.button
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleToggleTag(selectedTag)}
                    className="px-3 py-2 rounded-full bg-aw-green/85 text-aw-bluedark text-sm font-semibold flex items-center gap-2 hover:bg-aw-green transition duration-300"
                  >
                    {selectedTag}
                    <FontAwesomeIcon
                      icon="fa-solid fa-times"
                      className="text-xs"
                    />
                  </Motion.button>
                </div>
              </div>
            )}

            {/* Available Tags Grid */}
            <div>
              <h4 className="text-gray-300 font-medium mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon="fa-solid fa-tags" />
                Categorías Disponibles
                {filteredTags.length > 0 && (
                  <span className="text-sm text-gray-500">
                    ({filteredTags.length})
                  </span>
                )}
              </h4>
              <div className="flex flex-wrap gap-2 max-h-[40vh] overflow-y-auto scrollbar-box thin pr-2">
                {filteredTags.length > 0 ? (
                  filteredTags.map((tag, i) => {
                    const isSelected = selectedTag === tag;
                    return (
                      <Motion.button
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.02 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleToggleTag(tag)}
                        className={`px-3 py-2 rounded-full text-sm font-semibold transition duration-300 ${
                          isSelected
                            ? "bg-aw-green bg-opacity-30 text-aw-bluedark border-2 border-aw-green"
                            : "bg-aw-bluedark text-white border-2 border-gray-600 hover:border-aw-green"
                        }`}
                      >
                        {tag}
                        {isSelected && (
                          <FontAwesomeIcon
                            icon="fa-solid fa-check"
                            className="ml-2 text-xs"
                          />
                        )}
                      </Motion.button>
                    );
                  })
                ) : (
                  <div className="w-full text-center py-8 text-gray-400">
                    <FontAwesomeIcon
                      icon="fa-solid fa-search"
                      className="text-4xl mb-2"
                    />
                    <p>No se encontraron categorías</p>
                  </div>
                )}
              </div>
            </div>

            {/* Save and Cancel Buttons */}
            <div className="flex gap-3 pt-4">
              <Motion.button
                whileHover={{ scale: isSaving ? 1 : 1.02 }}
                whileTap={{ scale: isSaving ? 1 : 0.98 }}
                onClick={handleSave}
                disabled={isSaving || eventoLoading}
                className={`flex-1 py-3 font-bold rounded-lg transition duration-300 flex items-center justify-center gap-2 ${
                  saveSuccess
                    ? "bg-green-600 text-white"
                    : isSaving || eventoLoading
                    ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                    : "bg-aw-green/85 text-aw-bluedark hover:bg-aw-green"
                }`}
              >
                {isSaving || eventoLoading ? (
                  <>
                    <FontAwesomeIcon
                      icon="fa-solid fa-spinner"
                      className="animate-spin"
                    />
                    Guardando...
                  </>
                ) : saveSuccess ? (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-check" />
                    ¡Guardado!
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-save" />
                    Guardar Categoría
                  </>
                )}
              </Motion.button>
              <Motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCancel}
                disabled={isSaving || eventoLoading}
                className={`flex-1 py-3 font-bold rounded-lg transition duration-300 flex items-center justify-center gap-2 ${
                  isSaving || eventoLoading
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-gray-600 text-white hover:bg-gray-500"
                }`}
              >
                <FontAwesomeIcon icon="fa-solid fa-times" />
                Cancelar
              </Motion.button>
            </div>
          </Motion.div>
        ) : (
          <Motion.div
            key="preview-mode"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <h4 className="text-white font-bold text-xl text-center mb-4">
              Categoría
            </h4>
            {previewTag ? (
              <div className="flex flex-wrap gap-2 p-2 bg-aw-bluegray/40 rounded-lg">
                <Motion.span
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="px-3 py-2 rounded-full text-sm font-semibold bg-aw-green text-aw-bluedark"
                >
                  {previewTag}
                </Motion.span>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <FontAwesomeIcon
                  icon="fa-solid fa-tags"
                  className="text-4xl mb-2"
                />
                <p>No hay categoría seleccionada</p>
                <p className="text-sm mt-1">
                  Haz clic en "Editar" para agregar
                </p>
              </div>
            )}
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
